import {
  FocalEventPosterImage,
  getCoverEventPosterImage,
} from '@/features/events/shared/FocalEventPosterImage';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { formatDealDays, formatDealTimeRange, getDealTiming } from '@/features/venues/deals/deals';
import { getVenueCategoryConfig, resolveVenueCategory } from '@/features/venues/venueCategories';
import { formatEventDayLabel, formatEventTime } from '@/features/events/eventFormatting';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { cn } from '@/lib/utils';
import type { VenueSummary } from '@/lib/api/apiTypes';
import { Image } from 'expo-image';
import {
  CalendarDaysIcon,
  TicketPercentIcon,
  UserRoundIcon,
  type LucideIcon,
} from 'lucide-react-native';
import { Pressable, View } from 'react-native';
import type { SmartSearchResult } from './smartSearchResults';

type ResultContent = {
  icon: LucideIcon;
  meta: string;
  subtitle: string;
  title: string;
  typeLabel: string;
  isLiveDeal: boolean;
};

function getResultContent(result: SmartSearchResult): ResultContent {
  switch (result.type) {
    case 'event':
      return {
        icon: CalendarDaysIcon,
        title: result.item.title,
        subtitle: result.item.venue_name || 'Venue TBD',
        meta: [
          result.reason,
          `${formatEventDayLabel(result.item.starts_at)} · ${formatEventTime(result.item.starts_at)}`,
          result.moreCount ? `+${result.moreCount} more dates` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        typeLabel: 'Event',
        isLiveDeal: false,
      };
    case 'deal': {
      const timing = getDealTiming(result.item);
      const isLive = timing.state === 'live';
      return {
        icon: TicketPercentIcon,
        title: result.item.title,
        subtitle: result.item.venue_name,
        meta: [
          result.reason,
          isLive
            ? 'Live now'
            : `${formatDealDays(result.item.active_days)} · ${formatDealTimeRange(result.item)}`,
        ].join(' · '),
        typeLabel: 'Deal',
        isLiveDeal: isLive,
      };
    }
    case 'profile':
      return {
        icon: UserRoundIcon,
        title: result.item.display_name || result.item.username,
        subtitle: `@${result.item.username}`,
        meta: [result.reason, result.item.is_limited ? 'Friends only' : null]
          .filter(Boolean)
          .join(' · '),
        typeLabel: 'Profile',
        isLiveDeal: false,
      };
    case 'venue': {
      const category = resolveVenueCategory(result.item.category);
      return {
        icon: CalendarDaysIcon,
        title: result.item.name,
        subtitle: result.item.address || 'Venue',
        meta: [result.reason, category ? getVenueCategoryConfig(category).label : null]
          .filter(Boolean)
          .join(' · '),
        typeLabel: 'Venue',
        isLiveDeal: false,
      };
    }
  }
}

function getTypeBadge(type: SmartSearchResult['type'], isLiveDeal: boolean) {
  if (type === 'deal' && isLiveDeal) {
    return { bg: 'bg-status-soon/20 border-status-soon/40', text: 'text-status-soon' };
  }
  if (type === 'event') {
    return { bg: 'bg-primary-soft border-primary-edge', text: 'text-primary' };
  }
  return { bg: 'bg-canvas-inset border-border', text: 'text-muted-foreground' };
}

function VenueAvatar({ venue }: { venue: VenueSummary }) {
  const cfg = getVenueCategoryConfig(resolveVenueCategory(venue.category));
  const bg = '#' + cfg.bgColor;
  const color = '#' + cfg.color;

  if (venue.logo_url) {
    return (
      <View
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          borderWidth: 2,
          borderColor: color + '55',
          overflow: 'hidden',
          backgroundColor: bg,
        }}>
        <Image
          source={{ uri: venue.logo_url }}
          style={{ width: 40, height: 40 }}
          cachePolicy="memory-disk"
          contentFit="cover"
        />
      </View>
    );
  }

  return (
    <View
      style={{
        width: 44,
        height: 44,
        borderRadius: 22,
        backgroundColor: bg,
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Icon as={cfg.icon} size={18} style={{ color }} />
    </View>
  );
}

export function SmartSearchResultRow({
  onPress,
  result,
}: {
  onPress: () => void;
  result: SmartSearchResult;
}) {
  const content = getResultContent(result);
  const badge = getTypeBadge(result.type, content.isLiveDeal);
  const posterImage = result.type === 'event' ? getCoverEventPosterImage(result.item) : null;

  return (
    <Pressable
      onPress={onPress}
      className={cn(
        'flex-row items-center gap-3 rounded-[20px] border bg-surface-alt px-4 py-3.5 active:opacity-85',
        content.isLiveDeal ? 'border-status-soon' : 'border-border'
      )}>
      {result.type === 'event' && posterImage ? (
        <FocalEventPosterImage image={posterImage} className="h-14 w-11 shrink-0 rounded-xl bg-canvas-inset" />
      ) : result.type === 'event' ? (
        <View className="size-11 shrink-0 items-center justify-center rounded-xl bg-primary-soft">
          <Icon as={content.icon} size={18} className="text-primary" />
        </View>
      ) : result.type === 'venue' ? (
        <View className="shrink-0">
          <VenueAvatar venue={result.item} />
        </View>
      ) : result.type === 'profile' ? (
        <Avatar alt={content.title} className="size-11 shrink-0">
          {result.item.avatar_url ? (
            <AvatarImage source={{ uri: result.item.avatar_url }} />
          ) : null}
          <AvatarFallback className="bg-primary-soft">
            <Text className="text-base font-semibold text-foreground">
              {getProfileInitials(content.title)}
            </Text>
          </AvatarFallback>
        </Avatar>
      ) : (
        <View className="size-11 shrink-0 items-center justify-center rounded-xl bg-canvas-inset">
          <Icon as={content.icon} size={18} className="text-muted-foreground" />
        </View>
      )}

      <View className="min-w-0 flex-1 gap-0.5">
        <View className="flex-row items-center gap-2">
          <Text
            className="min-w-0 flex-1 text-base font-semibold text-foreground"
            numberOfLines={1}>
            {content.title}
          </Text>
          <View className={cn('shrink-0 rounded-full border px-2 py-0.5', badge.bg)}>
            <Text variant="small" className={cn('font-semibold', badge.text)}>
              {content.typeLabel}
            </Text>
          </View>
        </View>
        <Text variant="muted" className="leading-5" numberOfLines={1}>
          {content.subtitle}
        </Text>
        <Text variant="small" className="text-muted-foreground" numberOfLines={1}>
          {content.meta}
        </Text>
      </View>
    </Pressable>
  );
}
